/**
 * Reporting Core SDK - React Hooks
 *
 * Headless React hooks for integrating the Reporting API.
 * These hooks do not render UI - they expose state and actions
 * for host applications to build their own UI.
 */

import { useCallback, useEffect, useMemo, useRef, useState } from 'react';

import { createReportingClient, ReportingClient } from './client';
import {
  FilterOptionsCache,
  globalOptionsCache,
  initializeFilterValues,
  isQueryBackedFilter,
  prepareFiltersForSubmission,
  validateFilterValues,
} from './filters';
import type {
  AsyncState,
  AuthProviderConfig,
  ExportMetadata,
  Filter,
  FilterOption,
  FilterValues,
  HtmlResponse,
  ListReportsData,
  Report,
  ReportConfig,
  ReportData,
  ReportingClientConfig,
} from './types';
import { ReportingApiError } from './types';

// ============================================================================
// Context Types
// ============================================================================

/**
 * SDK context value for provider pattern.
 */
export interface ReportingContextValue {
  client: ReportingClient;
  optionsCache: FilterOptionsCache;
}

// ============================================================================
// useReports Hook
// ============================================================================

/**
 * State and actions for the useReports hook.
 */
export interface UseReportsResult extends AsyncState<Report[]> {
  /** Refresh the reports list */
  refresh: () => Promise<void>;
}

/**
 * Hook for listing available reports.
 *
 * @example
 * ```tsx
 * const { data: reports, loading, error, refresh } = useReports({
 *   baseUrl: 'http://localhost:4000/api/v1',
 *   authProvider: { getAccessToken: () => token },
 * });
 *
 * if (loading) return <Spinner />;
 * if (error) return <Error message={error.message} />;
 *
 * return (
 *   <ul>
 *     {reports?.map(r => <li key={r.id}>{r.name}</li>)}
 *   </ul>
 * );
 * ```
 */
export function useReports(config: ReportingClientConfig): UseReportsResult {
  const [state, setState] = useState<AsyncState<Report[]>>({
    data: null,
    loading: true,
    error: null,
  });

  const clientRef = useRef<ReportingClient | null>(null);

  // Create client once
  if (!clientRef.current) {
    clientRef.current = createReportingClient(config);
  }

  const fetchReports = useCallback(async () => {
    setState((prev) => ({ ...prev, loading: true, error: null }));

    try {
      const result = await clientRef.current!.listReports();
      setState({ data: result.reports, loading: false, error: null });
    } catch (err) {
      const error =
        err instanceof ReportingApiError
          ? err
          : new ReportingApiError(err instanceof Error ? err.message : 'Unknown error');
      setState((prev) => ({ ...prev, loading: false, error }));
    }
  }, []);

  useEffect(() => {
    fetchReports();
  }, [fetchReports]);

  return {
    ...state,
    refresh: fetchReports,
  };
}

// ============================================================================
// useReportConfig Hook
// ============================================================================

/**
 * State and actions for the useReportConfig hook.
 */
export interface UseReportConfigResult extends AsyncState<ReportConfig> {
  /** Refresh the config */
  refresh: () => Promise<void>;
}

/**
 * Hook for loading report configuration.
 *
 * @param config - SDK client config
 * @param reportId - Report ID to load config for (null to skip)
 */
export function useReportConfig(
  config: ReportingClientConfig,
  reportId: string | null
): UseReportConfigResult {
  const [state, setState] = useState<AsyncState<ReportConfig>>({
    data: null,
    loading: false,
    error: null,
  });

  const clientRef = useRef<ReportingClient | null>(null);

  // Create client once
  if (!clientRef.current) {
    clientRef.current = createReportingClient(config);
  }

  const fetchConfig = useCallback(async () => {
    if (!reportId) {
      setState({ data: null, loading: false, error: null });
      return;
    }

    setState((prev) => ({ ...prev, loading: true, error: null }));

    try {
      const result = await clientRef.current!.getReportConfig(reportId);
      setState({ data: result, loading: false, error: null });
    } catch (err) {
      const error =
        err instanceof ReportingApiError
          ? err
          : new ReportingApiError(err instanceof Error ? err.message : 'Unknown error');
      setState((prev) => ({ ...prev, loading: false, error }));
    }
  }, [reportId]);

  useEffect(() => {
    fetchConfig();
  }, [fetchConfig]);

  return {
    ...state,
    refresh: fetchConfig,
  };
}

// ============================================================================
// useFilterState Hook
// ============================================================================

/**
 * State and actions for filter management.
 */
export interface UseFilterStateResult {
  /** Current filter values */
  values: FilterValues;

  /** Set a single filter value */
  setValue: (filterId: string, value: unknown) => void;

  /** Set multiple filter values */
  setValues: (updates: FilterValues) => void;

  /** Reset to initial defaults */
  reset: () => void;

  /** Validate current values (throws on error) */
  validate: () => void;

  /** Get prepared values for submission (throws on error) */
  getSubmitValues: () => FilterValues;
}

/**
 * Hook for managing filter state.
 *
 * Initializes from config defaults and provides validation.
 */
export function useFilterState(config: ReportConfig | null): UseFilterStateResult {
  const [values, setValues] = useState<FilterValues>({});

  // Initialize values when config changes
  useEffect(() => {
    if (config) {
      setValues(initializeFilterValues(config));
    } else {
      setValues({});
    }
  }, [config]);

  const setValue = useCallback((filterId: string, value: unknown) => {
    setValues((prev) => ({ ...prev, [filterId]: value }));
  }, []);

  const setMultipleValues = useCallback((updates: FilterValues) => {
    setValues((prev) => ({ ...prev, ...updates }));
  }, []);

  const reset = useCallback(() => {
    if (config) {
      setValues(initializeFilterValues(config));
    } else {
      setValues({});
    }
  }, [config]);

  const validate = useCallback(() => {
    if (config) {
      validateFilterValues(config.filters, values);
    }
  }, [config, values]);

  const getSubmitValues = useCallback(() => {
    if (config) {
      validateFilterValues(config.filters, values);
    }
    return prepareFiltersForSubmission(values);
  }, [config, values]);

  return {
    values,
    setValue,
    setValues: setMultipleValues,
    reset,
    validate,
    getSubmitValues,
  };
}

// ============================================================================
// useFilterOptions Hook
// ============================================================================

/**
 * State for a single filter's options.
 */
export interface FilterOptionsState {
  options: FilterOption[];
  loading: boolean;
  error: ReportingApiError | null;
}

/**
 * Result from useFilterOptions hook.
 */
export interface UseFilterOptionsResult {
  /** Options state by filter ID */
  optionsState: Record<string, FilterOptionsState>;

  /** Fetch options for a filter */
  fetchOptions: (filterId: string, dependencyValues?: FilterValues) => Promise<void>;

  /** Get options for a filter (from cache or state) */
  getOptions: (filterId: string) => FilterOption[];

  /** Check if a filter is loading */
  isLoading: (filterId: string) => boolean;

  /** Get error for a filter */
  getError: (filterId: string) => ReportingApiError | null;
}

/**
 * Hook for loading filter options with cascading support.
 *
 * @param config - SDK client config
 * @param reportId - Report ID
 * @param filters - Filter definitions
 * @param filterValues - Current filter values (for cascading)
 */
export function useFilterOptions(
  config: ReportingClientConfig,
  reportId: string | null,
  filters: Filter[],
  filterValues: FilterValues
): UseFilterOptionsResult {
  const [optionsState, setOptionsState] = useState<Record<string, FilterOptionsState>>({});

  const clientRef = useRef<ReportingClient | null>(null);
  const cacheRef = useRef(globalOptionsCache);

  // Create client once
  if (!clientRef.current) {
    clientRef.current = createReportingClient(config);
  }

  // Get query-backed filters
  const queryFilters = useMemo(() => filters.filter(isQueryBackedFilter), [filters]);

  const fetchOptions = useCallback(
    async (filterId: string, dependencyValues?: FilterValues) => {
      if (!reportId) return;

      const cacheKey = FilterOptionsCache.createKey(reportId, filterId, dependencyValues);
      const cached = cacheRef.current.get(cacheKey);

      if (cached) {
        setOptionsState((prev) => ({
          ...prev,
          [filterId]: { options: cached, loading: false, error: null },
        }));
        return;
      }

      setOptionsState((prev) => ({
        ...prev,
        [filterId]: { options: prev[filterId]?.options ?? [], loading: true, error: null },
      }));

      try {
        const result = await clientRef.current!.getFilterOptions(
          reportId,
          filterId,
          dependencyValues
        );

        cacheRef.current.set(cacheKey, result.options);

        setOptionsState((prev) => ({
          ...prev,
          [filterId]: { options: result.options, loading: false, error: null },
        }));
      } catch (err) {
        const error =
          err instanceof ReportingApiError
            ? err
            : new ReportingApiError(err instanceof Error ? err.message : 'Unknown error');

        setOptionsState((prev) => ({
          ...prev,
          [filterId]: { options: prev[filterId]?.options ?? [], loading: false, error },
        }));
      }
    },
    [reportId]
  );

  // Auto-fetch options for query-backed filters
  useEffect(() => {
    for (const filter of queryFilters) {
      // TODO: Determine dependency values based on filter config
      // For now, fetch with current filter values as potential dependencies
      fetchOptions(filter.id, filterValues);
    }
  }, [queryFilters, fetchOptions, filterValues]);

  const getOptions = useCallback(
    (filterId: string): FilterOption[] => {
      // Check static options first
      const filter = filters.find((f) => f.id === filterId);
      if (filter?.options) {
        return filter.options;
      }

      // Return dynamic options
      return optionsState[filterId]?.options ?? [];
    },
    [filters, optionsState]
  );

  const isLoading = useCallback(
    (filterId: string): boolean => optionsState[filterId]?.loading ?? false,
    [optionsState]
  );

  const getError = useCallback(
    (filterId: string): ReportingApiError | null => optionsState[filterId]?.error ?? null,
    [optionsState]
  );

  return {
    optionsState,
    fetchOptions,
    getOptions,
    isLoading,
    getError,
  };
}

// ============================================================================
// useGenerateReport Hook
// ============================================================================

/**
 * Result from useGenerateReport hook.
 */
export interface UseGenerateReportResult {
  /** Generate JSON report data */
  generateJson: (
    filters: FilterValues,
    options?: { page?: number; pageSize?: number }
  ) => Promise<ReportData | null>;

  /** Generate HTML preview with pagination metadata */
  generateHtml: (
    filters: FilterValues,
    options?: { page?: number; pageSize?: number }
  ) => Promise<HtmlResponse | null>;

  /** Generate CSV export */
  generateCsv: (
    filters: FilterValues,
    options?: { pageSize?: number }
  ) => Promise<ExportMetadata | null>;

  /** Current loading state */
  loading: boolean;

  /** Last error */
  error: ReportingApiError | null;

  /** Last JSON result */
  jsonData: ReportData | null;

  /** Last HTML result */
  htmlData: HtmlResponse | null;

  /** Last CSV metadata */
  csvData: ExportMetadata | null;

  /** Clear all results */
  clear: () => void;
}

/**
 * Hook for generating reports in various formats.
 *
 * @param config - SDK client config
 * @param reportId - Report ID
 */
export function useGenerateReport(
  config: ReportingClientConfig,
  reportId: string | null
): UseGenerateReportResult {
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<ReportingApiError | null>(null);
  const [jsonData, setJsonData] = useState<ReportData | null>(null);
  const [htmlData, setHtmlData] = useState<HtmlResponse | null>(null);
  const [csvData, setCsvData] = useState<ExportMetadata | null>(null);

  const clientRef = useRef<ReportingClient | null>(null);

  // Create client once
  if (!clientRef.current) {
    clientRef.current = createReportingClient(config);
  }

  const generateJson = useCallback(
    async (
      filters: FilterValues,
      options?: { page?: number; pageSize?: number }
    ): Promise<ReportData | null> => {
      if (!reportId) return null;

      setLoading(true);
      setError(null);

      try {
        const result = await clientRef.current!.generateReportJson(
          reportId,
          prepareFiltersForSubmission(filters),
          options
        );
        setJsonData(result);
        return result;
      } catch (err) {
        const apiError =
          err instanceof ReportingApiError
            ? err
            : new ReportingApiError(err instanceof Error ? err.message : 'Unknown error');
        setError(apiError);
        return null;
      } finally {
        setLoading(false);
      }
    },
    [reportId]
  );

  const generateHtml = useCallback(
    async (
      filters: FilterValues,
      options?: { page?: number; pageSize?: number }
    ): Promise<HtmlResponse | null> => {
      if (!reportId) return null;

      setLoading(true);
      setError(null);

      try {
        const result = await clientRef.current!.generateReportHtml(
          reportId,
          prepareFiltersForSubmission(filters),
          options
        );
        setHtmlData(result);
        return result;
      } catch (err) {
        const apiError =
          err instanceof ReportingApiError
            ? err
            : new ReportingApiError(err instanceof Error ? err.message : 'Unknown error');
        setError(apiError);
        return null;
      } finally {
        setLoading(false);
      }
    },
    [reportId]
  );

  const generateCsv = useCallback(
    async (
      filters: FilterValues,
      options?: { pageSize?: number }
    ): Promise<ExportMetadata | null> => {
      if (!reportId) return null;

      setLoading(true);
      setError(null);

      try {
        const result = await clientRef.current!.generateReportCsv(
          reportId,
          prepareFiltersForSubmission(filters),
          options
        );
        setCsvData(result);
        return result;
      } catch (err) {
        const apiError =
          err instanceof ReportingApiError
            ? err
            : new ReportingApiError(err instanceof Error ? err.message : 'Unknown error');
        setError(apiError);
        return null;
      } finally {
        setLoading(false);
      }
    },
    [reportId]
  );

  const clear = useCallback(() => {
    setJsonData(null);
    setHtmlData(null);
    setCsvData(null);
    setError(null);
  }, []);

  // Clear data when report changes
  useEffect(() => {
    clear();
  }, [reportId, clear]);

  return {
    generateJson,
    generateHtml,
    generateCsv,
    loading,
    error,
    jsonData,
    htmlData,
    csvData,
    clear,
  };
}

// ============================================================================
// Convenience Token Provider
// ============================================================================

/**
 * Create an auth provider from a static token string.
 *
 * Useful for simple token-based auth where the token is already available.
 */
export function createTokenAuthProvider(token: string): AuthProviderConfig {
  return {
    getAccessToken: () => token,
  };
}
